import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Landmark, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_CHOICES = ['2 à 5 organisations', '6 à 10 organisations', 'Plus de 10 organisations'];
const BUDGET_CHOICES = [
    { label: 'Moins de 100 000 €', value: 100000 },
    { label: '100 000 € – 500 000 €', value: 500000 },
    { label: '500 000 € – 2 M€', value: 2000000 },
    { label: 'Plus de 2 M€', value: 5000000 },
];

const BENEFITS = [
    "Jusqu'à 10 organisations bénéficiaires hébergées sur une infrastructure unique",
    'Tableau de bord consolidé multi-organisations pour le bailleur',
    'Certificats Campus 360° pour toutes les équipes accompagnées',
    'Accompagnement ADRNS inclus (missions de conseil structurées)',
    'White-label & packs sectoriels inclus',
    'Support dédié et onboarding de chaque bénéficiaire',
];

export default function Consortium() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        full_name: '', email: '', organization: '', country: '', phone: '',
        nb_orgs: ORG_CHOICES[0], budget: BUDGET_CHOICES[1].value, message: '', website: '',
    });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name.trim() || !form.email.trim() || !form.organization.trim()) {
            toast.error('Nom, email et organisation sont requis.');
            return;
        }
        setBusy(true);
        try {
            const budgetLabel = BUDGET_CHOICES.find(b => b.value === Number(form.budget))?.label || form.budget;
            await axios.post(`${API}/access-requests/create`, {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                organization: form.organization.trim(),
                country: form.country.trim() || null,
                phone: form.phone.trim() || null,
                source: 'consortium',
                team_size: form.nb_orgs,
                funds_to_manage_amount: Number(form.budget),
                funds_to_manage_currency: 'EUR',
                message: `🤝 OFFRE CONSORTIUM · Organisations bénéficiaires : ${form.nb_orgs} · Budget programme : ${budgetLabel}`
                    + (form.message.trim() ? `\n\n${form.message.trim()}` : ''),
                website: form.website,
            });
            setSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-consortium">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="consortium-logo-home">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover" />
                        <span className="font-heading text-2xl"><span className="text-foreground">African</span> <span className="text-primary">Valley</span></span>
                    </Link>
                    <Link to="/p/tarifs" className="text-sm text-foreground/70 hover:text-foreground">← Retour aux tarifs</Link>
                </div>
            </header>

            <main className="flex-1">
                <section className="container-page py-14 lg:py-18">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <div className="eyebrow mb-3">Bailleurs · Programmes · Consortiums</div>
                            <h1 className="font-heading text-4xl sm:text-5xl mb-5" data-testid="consortium-title">
                                🤝 L'offre <span className="text-primary">Consortium</span> pour piloter tout un portefeuille.
                            </h1>
                            <p className="text-foreground/70 leading-relaxed mb-6">
                                Vous financez ou coordonnez plusieurs organisations en Afrique ? Hébergez tous vos
                                bénéficiaires sur African Valley et pilotez l'ensemble depuis un tableau de bord consolidé —
                                gouvernance, budgets, GED, formation certifiante et accompagnement inclus.
                            </p>
                            <div className="card-soft p-6 mb-6">
                                <div className="flex items-baseline gap-2 mb-4">
                                    <Landmark size={20} className="text-secondary" />
                                    <span className="font-heading text-3xl">2 500 €</span>
                                    <span className="text-sm text-muted-foreground">/ an · jusqu'à 10 organisations</span>
                                </div>
                                <ul className="space-y-2 text-sm text-foreground/80">
                                    {BENEFITS.map(b => (
                                        <li key={b} className="flex items-start gap-2">
                                            <Check size={15} className="text-secondary mt-0.5 shrink-0" /> {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Au-delà de 10 organisations : devis adapté. Paiement possible en FCFA (virement, Mobile Money).
                            </p>
                        </div>

                        <div className="card-soft p-7 lg:p-8" data-testid="consortium-form-card">
                            {sent ? (
                                <div className="text-center py-10" data-testid="consortium-success">
                                    <div className="text-5xl mb-4">✅</div>
                                    <h2 className="font-heading text-2xl mb-3">Demande bien reçue !</h2>
                                    <p className="text-foreground/70 mb-6 leading-relaxed">
                                        Notre équipe vous recontacte sous 24 h ouvrées pour qualifier votre programme
                                        et préparer une démonstration consolidée.
                                    </p>
                                    <Button onClick={() => navigate('/')} variant="outline" data-testid="consortium-back-home">
                                        Retour à l'accueil
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <h2 className="font-heading text-xl mb-1">Qualifions votre programme</h2>
                                    <p className="text-sm text-muted-foreground mb-4">Réponse sous 24 h ouvrées.</p>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="c-name">Nom & prénom *</Label>
                                            <Input id="c-name" value={form.full_name} onChange={set('full_name')} required data-testid="consortium-name" />
                                        </div>
                                        <div>
                                            <Label htmlFor="c-email">Email professionnel *</Label>
                                            <Input id="c-email" type="email" value={form.email} onChange={set('email')} required data-testid="consortium-email" />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="c-org">Organisation / Bailleur *</Label>
                                            <Input id="c-org" value={form.organization} onChange={set('organization')} required data-testid="consortium-org" />
                                        </div>
                                        <div>
                                            <Label htmlFor="c-country">Pays d'intervention</Label>
                                            <Input id="c-country" value={form.country} onChange={set('country')} placeholder="ex. Congo, Sénégal…" data-testid="consortium-country" />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="c-orgs">Organisations bénéficiaires</Label>
                                            <select id="c-orgs" value={form.nb_orgs} onChange={set('nb_orgs')}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                                data-testid="consortium-nb-orgs">
                                                {ORG_CHOICES.map(o => <option key={o} value={o}>{o}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="c-budget">Budget annuel du programme</Label>
                                            <select id="c-budget" value={form.budget} onChange={set('budget')}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                                data-testid="consortium-budget">
                                                {BUDGET_CHOICES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="c-phone">Téléphone (WhatsApp accepté)</Label>
                                        <Input id="c-phone" value={form.phone} onChange={set('phone')} data-testid="consortium-phone" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-msg">Votre programme en quelques mots</Label>
                                        <Textarea id="c-msg" rows={3} value={form.message} onChange={set('message')}
                                            placeholder="Secteurs, pays, calendrier, attentes…" data-testid="consortium-message" />
                                    </div>
                                    <input type="text" value={form.website} onChange={set('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                                    <Button type="submit" disabled={busy} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="consortium-submit">
                                        {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <ArrowRight size={15} className="mr-2" />}
                                        Demander une démonstration consolidée
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
